import { ReactNode } from 'react';

import { ListItemText, MenuItem, TextField } from '@material-ui/core';

import { MetricsV1AggregationType as AggregationType } from '@spotify/confidence-graphql';

import { getAggregationTypeLabel } from './aggregationUtils';

// Aggregation types that exist in the schema but are not yet available in the UI
const HIDDEN_AGGREGATION_TYPES: AggregationType[] = [
  AggregationType.AggregationTypeApproxCountDistinct,
];

type AggregationTypeSelectProps = {
  entityName?: string;
  measurementName?: string;
  value: AggregationType;
  onChange: (type: AggregationType) => void;
  enabledOptions?: AggregationType[];
  'data-testid'?: string;
  helperText?: ReactNode;
  label?: string;
  disabled?: boolean;
};

const aggregationDescription: Record<
  AggregationType,
  (
    args: Pick<AggregationTypeSelectProps, 'entityName' | 'measurementName'>,
  ) => ReactNode
> = {
  [AggregationType.AggregationTypeAvg]: ({ entityName, measurementName }) =>
    `Average ${measurementName || 'value'} per ${entityName}.`,

  [AggregationType.AggregationTypeCount]: ({ measurementName, entityName }) =>
    `Amount of non-NULL ${measurementName || 'values'} per ${entityName}.`,

  [AggregationType.AggregationTypeCountDistinct]: ({
    measurementName,
    entityName,
  }) =>
    `Amount of unique non-NULL ${
      measurementName || 'values'
    } per ${entityName}.`,

  [AggregationType.AggregationTypeApproxCountDistinct]: ({
    measurementName,
    entityName,
  }) =>
    `Approximate count of unique non-NULL ${
      measurementName || 'values'
    } per ${entityName}.`,

  [AggregationType.AggregationTypeMax]: ({ measurementName, entityName }) =>
    `Highest ${measurementName || 'value'} per ${entityName}.`,

  [AggregationType.AggregationTypeMin]: ({ measurementName, entityName }) =>
    `Lowest ${measurementName || 'value'} per ${entityName}.`,

  [AggregationType.AggregationTypeSum]: ({ measurementName, entityName }) =>
    `Total of ${measurementName || 'values'} per ${entityName}.`,
};

export const AggregationTypeSelect = ({
  value,
  onChange,
  entityName,
  measurementName,
  enabledOptions,
  helperText,
  'data-testid': dataTestId,
  label = 'Method',
  disabled,
}: AggregationTypeSelectProps) => {
  return (
    <TextField
      select
      label={label}
      required
      fullWidth
      name="aggregation-type"
      data-testid={dataTestId ?? `aggregation-type-select-container`}
      value={value}
      onChange={e => onChange((e.target.value as AggregationType) ?? undefined)}
      helperText={helperText}
      disabled={disabled}
      variant="outlined"
      SelectProps={{
        renderValue: selected =>
          getAggregationTypeLabel(selected as AggregationType),
      }}
     data-track-source-anchor="af2eaa3fb2;aggregation-type-select-text-field">
      {Object.values(AggregationType)
        .filter(option =>
          enabledOptions
            ? enabledOptions.includes(option)
            : !HIDDEN_AGGREGATION_TYPES.includes(option),
        )
        .map(option => (
          <MenuItem key={option} value={option} data-track-source-anchor="6d506f533c;aggregation-type-select-menu-item;low">
            <ListItemText
              primary={getAggregationTypeLabel(option)}
              secondary={aggregationDescription[option]({
                measurementName: measurementName || '',
                entityName: entityName || 'entity',
              })}
            />
          </MenuItem>
        ))}
    </TextField>
  );
};
